import { format, parseISO, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Planning, Template, Shift } from './types';

const hexToRgb = (hex: string): [number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
};


/**
 * En-tête commun aux deux exports PDF du planning (maquette Exports Planning, 21a et 21b).
 *
 * La maquette pose trois règles, et rien de plus : LE TITRE EST CENTRÉ (établissement puis intitulé),
 * L'INTITULÉ ET LE FILET SONT À L'ENCRE `acc` (#384049), et pour le reste « seules la typographie, les
 * filets et les marges changent » — les colonnes, les options et le contenu ne bougent pas.
 *
 * Cette encre ne suit PAS la couleur choisie à l'export. La maquette distingue deux variables : `acc`
 * pour l'en-tête, `headBg` (#c1d5af dans l'exemple) pour le FOND de la ligne d'en-tête du tableau. Un
 * premier jet les avait confondues : l'intitulé sortait en vert pâle sur blanc, à peine lisible — et
 * le blanc, qui fait partie de la palette proposée, l'aurait rendu franchement invisible. La couleur
 * choisie continue de teinter la ligne d'en-tête du tableau, qui est sa place.
 *
 * Une seule définition pour la feuille de présence et pour le planning : sans cela, les deux documents
 * dériveraient l'un de l'autre au premier ajustement.
 *
 * @returns l'ordonnée à laquelle le tableau peut commencer.
 */
/** L'encre `acc` de la maquette Exports Planning : intitulé et filet. */
const ENCRE_EN_TETE = '#384049';

export const dessinerEnTetePdf = (doc: any, options: {
    etablissement: string;
    /** « Feuille de présence — semaine du 07 septembre 2026 ». */
    intitule: string;
    largeurPage: number;
    /** Ordonnée du haut de l'en-tête (10 mm par défaut). */
    haut?: number;
    /** Marge latérale du tableau, pour que le filet fasse exactement sa largeur (10 mm par défaut). */
    margeX?: number;
}): number => {
    const { etablissement, intitule, largeurPage } = options;
    const haut = options.haut ?? 10;
    const margeX = options.margeX ?? 10;
    const [r, g, b] = hexToRgb(ENCRE_EN_TETE);
    const centre = largeurPage / 2;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(15, 20, 25);
    doc.text(etablissement || '', centre, haut + 5, { align: 'center' });

    doc.setFontSize(8.5);
    doc.setTextColor(r, g, b);
    // Capitales espacées, comme la maquette : c'est ce qui distingue l'intitulé du nom de la maison.
    doc.text(intitule.toUpperCase(), centre, haut + 10.5, { align: 'center', charSpace: 0.5 });

    doc.setFillColor(r, g, b);
    doc.rect(margeX, haut + 13.5, largeurPage - margeX * 2, 0.9, 'F');

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    return haut + 19;
};

const ABSENCE_COLORS: Record<string, { bg: [number, number, number], text: [number, number, number] }> = {
    'REPOS': { bg: [0, 0, 0], text: [255, 255, 255] },
    'Ecole': { bg: [239, 235, 233], text: [93, 64, 55] },
    'mise à dispo': { bg: [94, 232, 215], text: [255, 255, 255] },
    'MISE A DISPO': { bg: [94, 232, 215], text: [255, 255, 255] },
    'HAB/DES': { bg: [255, 235, 238], text: [211, 47, 47] },
    'AA': { bg: [255, 235, 238], text: [211, 47, 47] },
    'DEFAULT_ABSENCE': { bg: [255, 235, 238], text: [211, 47, 47] }
};

export const generatePlanningGridPDF = async (options: {
    planning: Planning;
    roles: { id: string; label: string }[];
    rolesOrder: string[];
    employees: any[];
    companyName?: string;
    headerBgColor?: string;
    headerTextColor?: string;
}) => {
    const { planning, roles, rolesOrder, employees, companyName = "Votre Établissement", headerBgColor = '#C1D5AF', headerTextColor = '#000000' } = options;
    
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    
    const headerColorRgb = hexToRgb(headerBgColor);
    const textColorRgb = hexToRgb(headerTextColor);

    const doc = new jsPDF('landscape');
    const weekStartDate = parseISO((planning as any).week_start ?? planning.weekStart);
    
    doc.setFontSize(18);
    doc.text(`Planning ${companyName} - ${planning.service}`, 14, 15);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Semaine du ${format(weekStartDate, 'dd MMMM yyyy', { locale: fr })} au ${format(addDays(weekStartDate, 6), 'dd MMMM yyyy', { locale: fr })}`, 14, 22);
    
    const weekDatesArr = Array.from({ length: 7 }, (_, i) => format(addDays(weekStartDate, i), 'yyyy-MM-dd'));
    const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    
    const bodyData: any[] = [];
    const selectedRoles = roles.map(r => r.id); // All roles for archive
    
    const sortedEmployees = employees.filter(e => selectedRoles.includes(e.position || 'GENERAL')).sort((a, b) => {
        const idxA = rolesOrder.indexOf(a.position || 'GENERAL');
        const idxB = rolesOrder.indexOf(b.position || 'GENERAL');
        if (idxA !== idxB) return idxA - idxB;
        return (a.last_name || '').localeCompare(b.last_name || '');
    });

    let currentRole = '';
    sortedEmployees.forEach(emp => {
        const role = emp.position || 'GENERAL';
        if (role !== currentRole) {
            currentRole = role;
            const label = String(roles.find(r => r.id === currentRole)?.label || role || '');
            bodyData.push([{ content: label.toUpperCase(), colSpan: 8, styles: { fillColor: headerColorRgb, textColor: textColorRgb, fontStyle: 'bold', halign: 'center', fontSize: 10 } }]);
        }

        const planningRow = planning.rows.find(r => r.employeeId === emp.id);
        const rowData: any[] = [String(`${(emp.last_name || '').toUpperCase()} ${emp.first_name || ''}`).trim()];

        weekDatesArr.forEach(date => {
            const s = planningRow?.shifts[date];
            if (!s?.segments?.length) { rowData.push(''); return; }
            
            const lines: string[] = [];
            s.segments.forEach(seg => {
                if (seg.type === 'horaire' && seg.start && seg.end) {
                    lines.push(`${seg.start}-${seg.end}`);
                    if (seg.label) lines.push(seg.label);
                } else if (seg.type === 'code' && seg.label) {
                    lines.push(seg.label);
                }
            });
            const text = String(lines.join('\n') || '');
            
            // Determine cell styling based on segment types present
            let styles: any = {};
            const hasAbsenceCode = s.segments.some(seg => seg.type === 'code' && seg.label);
            const hasHoraire = s.segments.some(seg => seg.type === 'horaire' && seg.start && seg.end);
            
            if (hasAbsenceCode && !hasHoraire) {
                // Pure absence (full day) - apply absence color based on the code
                const label = s.segments.find(seg => seg.type === 'code')?.label || '';
                const style = ABSENCE_COLORS[label] || ABSENCE_COLORS['DEFAULT_ABSENCE'];
                styles = { fillColor: style.bg, textColor: style.text };
            } else if (hasAbsenceCode && hasHoraire) {
                // Mixed: work + half-day absence (e.g. coupure with AA on one half)
                // Use a light amber background to indicate mixed status
                styles = { fillColor: [255, 248, 225], textColor: [0, 0, 0] };
            } else if (hasHoraire) {
                // Pure work shift
                const firstHoraire = s.segments.find(seg => seg.type === 'horaire');
                const color = firstHoraire?.colorOverride || firstHoraire?.color;
                if (color) { 
                    const bg = hexToRgb(color);
                    const luminance = (0.299 * bg[0] + 0.587 * bg[1] + 0.114 * bg[2]) / 255;
                    const textColor = luminance > 0.65 ? [0, 0, 0] : [255, 255, 255];
                    styles = { fillColor: bg, textColor: textColor }; 
                }
            }
            
            if (Object.keys(styles).length) rowData.push({ content: text, styles });
            else rowData.push(text);
        });
        bodyData.push(rowData);
    });

    // KPI ROW
    const kpiRow = ['TOTAL STAFF'];
    weekDatesArr.forEach(date => {
        let midi = 0, soir = 0;
        planning.rows.forEach(r => {
            const s = r.shifts[date];
            s?.segments?.forEach(seg => {
                if (seg.type === 'horaire' && seg.start && seg.end) {
                    if (seg.start < "16:00") midi++;
                    if (seg.end >= "16:00" || seg.end < seg.start) soir++;
                }
            });
        });
        (planning.extraShifts || []).forEach(e => {
            if (e.date === date) {
                if (e.start < "16:00") midi += e.count;
                if (e.end >= "16:00" || e.end < e.start) soir += e.count;
            }
        });
        kpiRow.push(`M: ${midi} / S: ${soir}`);
    });
    bodyData.push(kpiRow);

    autoTable(doc, {
        startY: 30,
        head: [['Employé', ...days]],
        body: bodyData,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak', halign: 'center', valign: 'middle' },
        headStyles: { fillColor: headerColorRgb, textColor: textColorRgb, fontStyle: 'bold' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold', cellWidth: 40 } }
    });

    return doc;
};
